import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { driverService } from '../../application/DriverService';
import { Driver } from '../../domain/driver/Driver';

import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActivePlan } from '../plan/useRetrieveActivePlan';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../right-panel/useRetrieveRightPanelInfo';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UnlockDriversParams = {
  drivers: Array<Driver>;
};

type UseUnlockDriversProps = ServiceResponse<({ drivers }: UnlockDriversParams) => void, void>;

// Hooks act as 'Adapter' layer.
export const useUnlockDrivers = (): UseUnlockDriversProps => {
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { detailType, detailId } = useRetrieveRightPanelInfo();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Array<Driver>, unknown, UnlockDriversParams>(
    ({ drivers }: UnlockDriversParams): Promise<Array<Driver>> => {
      return driverService.unlockDrivers({ drivers, project: activeProject! });
    },
    {
      onSuccess: (updatedDrivers) => {
        console.info(`updatedDrivers`, updatedDrivers);

        if (detailType === `driver` && detailId && updatedDrivers.map((d) => d.id).includes(detailId)) {
          queryClient.invalidateQueries({ queryKey: [`planner_driver`, detailId] });
        }

        queryClient.invalidateQueries({ queryKey: [`planner_active_plan_drivers`, activePlan?.id] });

        showSnackbarMessage({
          type: `success`,
          messageIntlId: `infoerror.updateroutessuccess`,
        });
      },
      onError: (err: any) => {
        console.info(`Unlock Drivers Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ drivers }: UnlockDriversParams) => mutate({ drivers }),
  };
};
